/**
 * Aturan pembatalan & refund (mirror dari backend services/order_cancellation_service.py).
 * Frontend dan backend HARUS sejalan: tidak ada tombol yang lebih longgar/ketat dari API.
 */
export const MANUAL_PAYMENT_METHODS = ["bca", "mandiri", "bni", "bri", "qris"];
export const BITESHIP_COD_METHOD = "biteship_cod";
export const ADMIN_STATUS_TRANSITIONS = {
  pending_payment: ["paid"], paid: ["processing"], processing: ["shipped"], shipped: ["completed"], completed: [], cancelled: [],
};
// Biteship COD (mirror services/biteship/cod.py COD_STATUS_TRANSITIONS): tanpa `paid`; cod_pending = menunggu konfirmasi toko.
export const COD_STATUS_TRANSITIONS = { cod_pending: ["processing"], processing: ["shipped"], shipped: ["completed"], completed: [], cancelled: [], cod_return_pending: [], cod_returned: [] };
export const isBiteshipCod = (order) => !!order && order.payment_method === BITESHIP_COD_METHOD;

/** "allowed" | "verification_pending" | "gateway_unsupported" | "not_allowed" */
export function cancelEligibility(order) {
  if (!order) return "not_allowed";
  const st = order.status;
  if (isBiteshipCod(order)) {
    // COD: tidak ada dana prepaid → cod_pending boleh dibatalkan; processing mengikuti guard shipment Biteship (tanpa refund gateway)
    if (st === "cod_pending") return "allowed";
    if (st === "processing" && order.provider_used === "biteship") {
      const shipState = (order.biteship_shipment && order.biteship_shipment.state) || "NOT_CREATED";
      return (order.awb || !["NOT_CREATED", "CONFIRMED_FAILED", "CANCELLED"].includes(shipState)) ? "shipment_active" : "allowed";
    }
    return "not_allowed";
  }
  if (st === "pending_payment") return order.payment_status === "pending_verification" ? "verification_pending" : "allowed";
  if (st === "paid") return MANUAL_PAYMENT_METHODS.includes(order.payment_method) ? "allowed" : "gateway_unsupported";
  if (st === "processing" && order.provider_used === "biteship") {
    // Biteship gagal/ditolak sebelum AWB → boleh dibatalkan (backend menegakkan guard yang sama secara atomik)
    const shipState = (order.biteship_shipment && order.biteship_shipment.state) || "NOT_CREATED";
    if (order.awb || !["NOT_CREATED", "CONFIRMED_FAILED", "CANCELLED"].includes(shipState)) return "shipment_active";
    return MANUAL_PAYMENT_METHODS.includes(order.payment_method) ? "allowed" : "gateway_unsupported";
  }
  return "not_allowed";
}

export function allowedAdminTargets(current, order) {
  return (isBiteshipCod(order) ? COD_STATUS_TRANSITIONS : ADMIN_STATUS_TRANSITIONS)[current] || [];
}

/** Order lama tanpa `refund` → NOT_REQUIRED. */
export function refundOf(order) {
  const r = (order && order.refund) || {};
  return { required: !!r.required, status: r.status || "NOT_REQUIRED", amount: r.amount ?? null, reference: r.reference || null,
    method: r.method || null, refunded_at: r.refunded_at || null };
}

export const REFUND_LABEL = {
  PENDING: { customer: "Refund menunggu diproses toko", admin: "Menunggu Pengembalian", cls: "bg-[#FFB703]/15 text-[#B5790F]" },
  PROCESSING: { customer: "Refund sedang diproses", admin: "Sedang Diproses", cls: "bg-blue-100 text-blue-700" },
  REFUNDED: { customer: "Refund telah dikembalikan", admin: "Selesai", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" },
};

/* ---------- Phase 3: lifecycle PENGAJUAN pembatalan (order.cancellation.request_status) ---------- */
export const REQUEST_LABEL = {
  REQUESTED: { customer: "Pengajuan pembatalan menunggu tinjauan toko", admin: "Menunggu Persetujuan", cls: "bg-[#FFB703]/15 text-[#B5790F]" },
  APPROVED: { customer: "Pengajuan pembatalan disetujui", admin: "Disetujui", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" },
  REJECTED: { customer: "Pengajuan pembatalan ditolak", admin: "Ditolak", cls: "bg-red-100 text-red-700" },
};

/** null bila order tidak pernah punya pengajuan. */
export function requestOf(order) {
  const c = (order && order.cancellation) || {};
  if (!c.request_status) return null;
  return { status: c.request_status, reason: c.request_reason || null, requested_at: c.requested_at || null,
    reviewed_at: c.reviewed_at || null, decision_note: c.decision_note || null, payment_received: c.payment_received ?? null };
}

/**
 * Aksi pembatalan CUSTOMER (mirror guard backend CANCELLATION_REQUEST_REQUIRED):
 * "request" (manual → ajukan) | "request_pending" | "direct" (gateway pending_payment) | "gateway_unsupported" | "not_allowed"
 */
export function customerCancelAction(order) {
  if (isBiteshipCod(order)) return order.status === "cod_pending" ? "direct" : "not_allowed"; // belum ada dana → batal langsung
  if (!order || !["pending_payment", "paid"].includes(order.status)) return "not_allowed";
  if (MANUAL_PAYMENT_METHODS.includes(order.payment_method)) {
    return (order.cancellation || {}).request_status === "REQUESTED" ? "request_pending" : "request";
  }
  return order.status === "pending_payment" ? "direct" : "gateway_unsupported";
}

/* ---------- Phase 4C: presentasi pembatalan terpadu (CUSTOMER / SELLER / SYSTEM) ----------
   Hanya mapping metadata backend (`cancellation.by_role/reason_code/request_status/...`) → teks.
   Tidak ada business logic; tidak ada field DB baru. null bila order tidak cancelled. */
export const PAYMENT_TIMEOUT_REASON = "Pembeli belum melakukan pembayaran dalam 1 jam.";

export function cancellationPresentation(order) {
  if (!order || order.status !== "cancelled") return null;
  const c = order.cancellation || {};
  const base = { cancelled_at: c.at || null, reason_code: c.reason_code || null, badge: null };
  if (c.by_role === "system" && c.reason_code === "PAYMENT_TIMEOUT") {
    return { ...base, source: "system", sourceLabel: "Sistem", heading: "Dibatalkan oleh Sistem",
      reason: PAYMENT_TIMEOUT_REASON, badge: "Timeout Pembayaran" };
  }
  if (c.by_role === "system") {
    return { ...base, source: "system", sourceLabel: "Sistem", heading: "Dibatalkan oleh Sistem", reason: c.reason || null };
  }
  if (c.by_role === "customer") {
    return { ...base, source: "customer", sourceLabel: "Pembeli", heading: "Dibatalkan atas permintaan pembeli", reason: c.reason || null };
  }
  // Pengajuan pembeli yang DISETUJUI seller (approve endpoint menulis payment_received; direct cancel tidak).
  if (c.request_status === "APPROVED" && c.payment_received != null) {
    return { ...base, source: "customer", sourceLabel: "Pembeli (disetujui toko)", heading: "Dibatalkan atas permintaan pembeli",
      reason: c.request_reason || c.reason || null };
  }
  if (c.by_role) {
    return { ...base, source: "seller", sourceLabel: "Penjual", heading: "Dibatalkan oleh Penjual", reason: c.reason || null,
      badge: c.reason_code === "SELLER_PAYMENT_NOT_FOUND" ? "Pembayaran Tidak Ditemukan" : null };
  }
  return { ...base, source: "unknown", sourceLabel: "-", heading: "Dibatalkan", reason: c.reason || null };
}
